import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { taxCodeEndpoints } from '@/lib/api/endpoints';
import { taxZoneKeys, taxGroupKeys } from '@/lib/queryKeys';

/**
 * Revert a tenant-created tax code. This is the exit door for the
 * city-tier pencil's Revert action: the BE soft-deletes the code,
 * unwinds the fork-clone group (re-points zones back to the parent
 * + soft-deletes the empty clone) when the code was the only
 * customization there, and falls back to a plain unlink + soft-delete
 * for codes that sat on a non-cloned group.
 *
 * Distinct from `useDeleteTaxCode` because that path is for the
 * catalog page (Ziptax-populated codes); revert is scoped to codes
 * the tenant created via the pencil flow (`isTenantCreated = true`).
 */
export function useRevertTaxCode() {
  const qc = useQueryClient();
  return useApiMutation<{ message: string }, string>({
    mutationFn: (id) => apiPost<{ message: string }>(operationsClient, taxCodeEndpoints.revert(id)),
    onSuccess: () => {
      toast.success('Tax customization reverted.');
      void qc.invalidateQueries({ queryKey: taxZoneKeys.all() });
      void qc.invalidateQueries({ queryKey: taxGroupKeys.all() });
    },
  });
}
